import Vue from 'vue';
import createAuth0Client from '@auth0/auth0-spa-js';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

let instance;

/** Returns the current instance of the SDK */
export const getAuthService = () => instance;

export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
      };
    },
    methods: {
      async handleRedirectCallback() {
        this.loading = true;
        let result;
        try {
          result = await this.auth0Client.handleRedirectCallback();
          this.isAuthenticated = true;
          this.user = await this.getUser();
        } catch (e) {
          this.error = e;
        }
        this.loading = false;
        return result;
      },
      async getUser() {
        const user = await this.auth0Client.getUser();
        const jwt = this.getJWT();
        const response = await this.$axios.post(
          `${window.env.APING_URL}/insights/save_fooluser/`,
          null,
          { headers: { Authorization: `Bearer ${jwt}` } },
        );
        user.pk = response.data.pk;
        user.email = user.email.toLowerCase();
        user.uid = user['https://www.fool.com/uid'];
        user.roles = response.data.roles;
        user.avatar = response.data.avatar|| user.picture;
        return user;
      },
      hasRoles(...roles) {
        return this.user.roles.some(role => roles.includes(role));
      },
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      logout(o) {
        return this.auth0Client.logout(o);
      },
      getJWT() {
        const { cache } = this.auth0Client.cacheManager;
        const [key] = cache.allKeys(); // If login successful, there is only 1 key which looks like: "@@auth0spajs@@::AABBCCDD::default::openid profile email"
        const value = cache.get(key);
        const jwt = value.body.access_token;
        return jwt;
      },
    },
    watch: {
      user(newUser) {
        window.user = newUser;
      },
    },
    async created() {
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        client_id: options.clientId,
        redirect_uri: redirectUri,
      });

      try {
        if (
          window.location.search.includes('code=') &&
          window.location.search.includes('state=')
        ) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.handleRedirectCallback();

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      }
      this.isAuthenticated = await this.auth0Client.isAuthenticated();
      if (this.isAuthenticated) {
        this.user = await this.getUser();
      }
      this.loading = false;
    },
  });

  return instance;
};
